import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, SquareIcon, TrendingUp, Shield, AlertTriangle,
  CheckCircle2, BarChart3, Activity, FileText, Download, ArrowLeft,
  Clock, Printer, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ZoneConfidenceCard, PriceElasticityCard, DataIntegrityFooter } from '@/components/DataCredibility';
import api from '@/services/api';

const VERDICT_STYLES = {
  'STRONG_FUNDAMENTALS': { bg: 'bg-green-600/20', text: 'text-green-400', label: 'Strong Fundamentals' },
  'STRONG_INVESTMENT': { bg: 'bg-green-600/20', text: 'text-green-400', label: 'Strong Fundamentals' }, // Legacy
  'SOLID_POSITION': { bg: 'bg-emerald-600/20', text: 'text-emerald-400', label: 'Solid Position' },
  'ATTRACTIVE_OPPORTUNITY': { bg: 'bg-emerald-600/20', text: 'text-emerald-400', label: 'Solid Position' }, // Legacy
  'CONDITIONAL_FIT': { bg: 'bg-gold-600/20', text: 'text-gold-400', label: 'Conditional Fit' },
  'MONITOR': { bg: 'bg-red-600/20', text: 'text-red-400', label: 'Requires Analysis' }
};

// Helper function to extract value from credibility-wrapped metrics
const getValue = (metric) => {
  if (metric && typeof metric === 'object' && 'value' in metric) {
    return metric.value;
  }
  return metric;
};

export const DealCardPage = () => {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [dealCard, setDealCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchDealCard();
  }, [propertyId, token]);
  
  const fetchDealCard = async () => {
    try {
      setLoading(true);
      const endpoint = token 
        ? `/deal-card/${propertyId}?token=${token}`
        : `/deal-card/${propertyId}`;
      const res = await api.get(endpoint);
      setDealCard(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load deal card');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportPDF = async () => {
    // Download PDF via authenticated request
    try {
      const token = localStorage.getItem('rashii_access_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${apiUrl}/api/deal-card/${propertyId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rashii-deal-card-${propertyId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to export PDF');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF');
    }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Deal Card...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="glass-card border-red-600/30 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 font-medium mb-2">Access Denied</p>
            <p className="text-muted-foreground text-sm mb-4">{error}</p>
            <Button onClick={() => navigate('/properties')} variant="outline">
              Browse Properties
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!dealCard) return null;
  
  const { property, market_intelligence, ai_summary, rashii_score, confidence_meter, generated_at } = dealCard;
  const verdict = market_intelligence?.rashii_verdict;
  const verdictStyle = VERDICT_STYLES[verdict?.verdict] || VERDICT_STYLES['MONITOR'];
  
  return (
    <div className="min-h-screen bg-background" data-testid="deal-card-page">
      <div className="noise-overlay" />
      
      {/* Header - Hidden in print */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/5 print:hidden">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate(-1)} className="hover:text-primary">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handlePrint} size="sm">
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button onClick={handleExportPDF} size="sm" className="bg-primary">
                <Download className="w-4 h-4 mr-2" /> 
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Deal Card Content - A4 optimized */}
      <div className="max-w-4xl mx-auto px-6 py-8 print:p-0 print:max-w-none" id="deal-card-content">
        <div className="bg-background border border-white/10 rounded-lg p-8 print:border-0 print:rounded-none">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-primary/20 text-primary uppercase text-xs">
                  {property.property_type}
                </Badge>
                <Badge className={`${verdictStyle.bg} ${verdictStyle.text} uppercase text-xs`}>
                  {verdictStyle.label}
                </Badge>
              </div>
              <h1 className="font-display text-2xl tracking-tight mb-1">{property.title}</h1>
              <div className="flex items-center text-muted-foreground text-sm">
                <MapPin className="w-4 h-4 mr-1" />
                {property.location}, {property.city}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-1">Asking Price</p>
              <p className="text-3xl font-display text-primary">
                AED {(property.price / 1000000).toFixed(1)}M
              </p>
              <p className="text-xs text-muted-foreground">
                {property.area_sqft?.toLocaleString()} sqft • AED {Math.round(property.price / property.area_sqft)}/sqft
              </p>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          {/* Score Section */}
          <div className="grid grid-cols-6 gap-4 mb-6">
            {/* Overall Score */}
            <div className="col-span-2 bg-secondary/30 rounded-lg p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Rashii Score</p>
              <div className="text-4xl font-display text-primary mb-1">{getValue(rashii_score?.overall_score) || 0}</div>
              <Progress value={getValue(rashii_score?.overall_score) || 0} className="h-1" />
            </div>
            
            {/* Score Breakdown */}
            <div className="col-span-4 grid grid-cols-4 gap-2">
              <ScoreItem label="ROI" value={getValue(rashii_score?.component_scores?.roi_potential) || getValue(rashii_score?.components?.roi_potential) || 0} />
              <ScoreItem label="Liquidity" value={getValue(rashii_score?.component_scores?.liquidity) || getValue(rashii_score?.components?.liquidity) || 0} />
              <ScoreItem label="Momentum" value={getValue(rashii_score?.component_scores?.market_momentum) || getValue(rashii_score?.components?.market_momentum) || 0} />
              <ScoreItem label="Risk" value={getValue(rashii_score?.component_scores?.risk_assessment) || getValue(rashii_score?.components?.risk_mitigation) || 0} />
            </div>
          </div>
          
          {/* Confidence Meter */}
          {confidence_meter && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <ConfidenceItem 
                label="Institutional Confidence" 
                value={confidence_meter.institutional_confidence?.level}
                score={confidence_meter.institutional_confidence?.score}
              />
              <ConfidenceItem 
                label="Liquidity Pressure" 
                value={confidence_meter.liquidity_pressure?.level}
                score={100 - (confidence_meter.liquidity_pressure?.avg_dom_score || 50)}
              />
              <ConfidenceItem 
                label="Deal Urgency" 
                value={confidence_meter.deal_urgency?.level}
                score={confidence_meter.deal_urgency?.urgency_score || 50}
              />
            </div>
          )}
          
          <Separator className="mb-6" />
          
          {/* Market Intelligence Summary */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Left: Pricing Analysis */}
            <div className="space-y-4">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Market Intelligence
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Benchmark</span>
                  <span className="text-sm font-medium">AED {getValue(market_intelligence?.area_benchmark_psf)?.toLocaleString()}/sqft</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Comparable Range</span>
                  <span className="text-sm font-medium">
                    AED {getValue(market_intelligence?.comparable_range?.min)?.toLocaleString()} - {getValue(market_intelligence?.comparable_range?.max)?.toLocaleString()}/sqft
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">vs Market</span>
                  <Badge className={
                    getValue(market_intelligence?.price_analysis?.price_vs_market_pct) < 0 
                      ? 'bg-green-600/20 text-green-400' 
                      : 'bg-gold-600/20 text-gold-400'
                  }>
                    {getValue(market_intelligence?.price_analysis?.price_vs_market_pct) > 0 ? '+' : ''}
                    {getValue(market_intelligence?.price_analysis?.price_vs_market_pct)}%
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Liquidity</span>
                  <div className="flex items-center gap-2">
                    <Progress value={getValue(market_intelligence?.demand_liquidity?.score) || 0} className="w-16 h-1" />
                    <Badge variant="outline" className="text-xs">
                      {getValue(market_intelligence?.demand_liquidity?.rating)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: Risk Flags */}
            <div className="space-y-4">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Shield className="w-4 h-4" /> Risk Flags (Top 3)
              </h3>
              
              <div className="space-y-2">
                {market_intelligence?.risk_flags?.slice(0, 3).map((risk, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      risk.severity === 'HIGH' ? 'text-red-400' :
                      risk.severity === 'MEDIUM' ? 'text-gold-400' : 'text-blue-400'
                    }`} />
                    <div>
                      <span className="font-medium">{risk.type?.replace('_', ' ')}</span>
                      <span className="text-muted-foreground ml-2">{risk.description}</span>
                    </div>
                  </div>
                )) || (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    No significant risk factors identified
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Rashii Verdict */}
          <div className={`p-4 rounded-lg border ${verdictStyle.bg} border-white/10 mb-6`}>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className={`w-5 h-5 ${verdictStyle.text}`} />
              <h3 className={`font-display text-lg ${verdictStyle.text}`}>
                Rashii Verdict: {verdict?.verdict?.replace('_', ' ')}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">{verdict?.summary}</p>
            
            {verdict?.key_insights?.length > 0 && (
              <div className="mt-3 space-y-1">
                {verdict.key_insights.slice(0, 3).map((insight, idx) => (
                  <p key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-primary">•</span> {insight}
                  </p>
                ))}
              </div>
            )}
          </div>
          
          {/* AI Summary */}
          {ai_summary && (
            <div className="bg-secondary/20 p-4 rounded-lg mb-6">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                <Activity className="w-4 h-4" /> AI Analysis Summary
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {ai_summary}
              </p>
            </div>
          )}
          
          {/* Zone Confidence & Price Elasticity Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Zone Confidence Index */}
            <ZoneConfidenceCard 
              zoneData={dealCard?.market_intelligence?.zone_confidence || {
                zone: property?.location || 'Dubai Marina',
                confidence_index: { value: 74 },
                liquidity_depth: 'High',
                capital_stability: 'Moderate',
                volatility: 'Controlled',
                momentum: 'Strengthening'
              }} 
            />
            
            {/* Price Elasticity - Only if unlocked */}
            {dealCard?.market_intelligence?.price_elasticity && (
              <PriceElasticityCard 
                elasticityData={dealCard.market_intelligence.price_elasticity} 
              />
            )}
          </div>
          
          {/* Data Integrity Footer */}
          <DataIntegrityFooter 
            integrityData={dealCard?.market_intelligence?.data_integrity || {
              primary_sources: ['DLD Transaction Records', 'Internal Analytics'],
              model_inputs: 1364,
              confidence_level: 'HIGH',
              generated_at: generated_at
            }}
          />
          
          <Separator className="my-4" />
          
          {/* Footer */}
          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="text-primary font-medium">RASHII VERIFIED</span>
              <span>Generated by Rashii — Verified Intelligence Exchange</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(generated_at).toLocaleString()}
              </span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground/60 text-center mt-2">
            For informational purposes only; final due diligence required. No seller identity disclosed.
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const ScoreItem = ({ label, value }) => {
  const displayValue = getValue(value);
  return (
    <div className="bg-secondary/20 rounded p-2 text-center">
      <p className="text-lg font-display text-primary">{displayValue || 0}</p>
      <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
    </div>
  );
};

const ConfidenceItem = ({ label, value, score }) => (
  <div className="bg-secondary/20 rounded p-3">
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
    <div className="flex items-center justify-between">
      <Badge variant="outline" className={`text-xs ${
        value === 'HIGH' ? 'border-green-600/30 text-green-400' :
        value === 'MODERATE' ? 'border-gold-600/30 text-gold-400' :
        'border-red-600/30 text-red-400'
      }`}>
        {value}
      </Badge>
      <Progress value={score || 50} className="w-12 h-1" />
    </div>
  </div>
);

export default DealCardPage;
